import React, { useState } from 'react';
import { Package, Users, Calendar, Plane, Hotel, Activity, Weight, CheckCircle2, Download } from 'lucide-react';
import { packingCategories, clothingAdvice, layeringSystem } from '../data/mockData';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './PackingGenerator.css';

function PackingGenerator() {
  const [formData, setFormData] = useState({
    country: 'thailand',
    travelers: 2,
    days: 14,
    transport: ['plane'],
    accommodation: ['hotel'],
    activities: ['beach', 'sightseeing'],
    packingPreference: 'lightweight'
  });
  
  const [packingList, setPackingList] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});

  const countries = ['Deutschland', 'Österreich', 'Liechtenstein', 'Schweiz', 'Italien', 'Ungarn', 'Tschechien', 'Kroatien', 'Bosnien', 'Teneriffa', 'El Hierro', 'Polen', 'Portugal', 'Jordanien', 'Oman', 'Dubai', 'Philippinen', 'Thailand', 'Hong Kong', 'Indien', 'Taiwan', 'New York'];
  const transportOptions = ['Auto', 'Flugzeug', 'Zug', 'Bus', 'Schiff'];
  const accommodationOptions = ['Hotel', 'Hostel', 'Appartment', 'Camping', 'Resort', 'Bei Freunden'];
  const activityOptions = ['Wandern', 'Radfahren', 'Tauchen', 'Schnorcheln', 'Schwimmen', 'Nachtleben', 'Strandurlaub', 'Städtereisen', 'Sightseeing', 'Fotografie', 'Wellness', 'Skifahren'];

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const currentValues = prev[field];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [field]: newValues };
    });
  };

  const generatePackingList = () => {
    const list = {
      basics: [...packingCategories.basics],
      clothing: [],
      toiletries: [...packingCategories.toiletries],
      tech: [...packingCategories.tech],
      activities: []
    };

    // Determine climate
    const tropicalCountries = ['thailand', 'philippines'];
    const coldCountries = ['skifahren'];
    
    if (tropicalCountries.some(c => formData.country.toLowerCase().includes(c))) {
      list.clothing = [...packingCategories.clothing.tropical];
    } else if (formData.activities.some(a => coldCountries.includes(a.toLowerCase()))) {
      list.clothing = [...packingCategories.clothing.cold];
    } else {
      list.clothing = [...packingCategories.clothing.moderate];
    }

    // Add activity-specific items
    if (formData.activities.includes('tauchen')) {
      list.activities.push(...packingCategories.activities.diving);
    }
    if (formData.activities.includes('wandern')) {
      list.activities.push(...packingCategories.activities.hiking);
    }
    if (formData.activities.includes('strandurlaub') || formData.activities.includes('schwimmen')) {
      list.activities.push(...packingCategories.activities.beach);
    }

    setPackingList(list);
    setCheckedItems({});
  };

  const toggleCheck = (category, index) => {
    const key = `${category}-${index}`;
    setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getClothingAdviceForCountry = () => {
    const country = formData.country.toLowerCase();
    
    if (clothingAdvice.conservative.countries.some(c => country.includes(c.toLowerCase()))) {
      return clothingAdvice.conservative;
    }
    if (clothingAdvice.moderate.countries.some(c => country.includes(c.toLowerCase()))) {
      return clothingAdvice.moderate;
    }
    return clothingAdvice.relaxed;
  };

  const getLayeringAdvice = () => {
    const tropicalCountries = ['thailand', 'philippines'];
    const coldCountries = ['deutschland', 'österreich', 'schweiz'];
    const country = formData.country.toLowerCase();
    
    if (tropicalCountries.some(c => country.includes(c))) return layeringSystem.tropical;
    if (coldCountries.some(c => country.includes(c)) && (formData.days > 30 || formData.activities.includes('skifahren'))) {
      return layeringSystem.cold;
    }
    return layeringSystem.moderate;
  };

  const advice = getClothingAdviceForCountry();
  const layering = getLayeringAdvice();

  const downloadPDF = () => {
    if (!packingList) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(22);
    doc.setTextColor(26, 83, 92); // Ocean blue
    doc.text('Just Travel - Packliste', pageWidth / 2, 20, { align: 'center' });
    
    // Trip details
    doc.setFontSize(12);
    doc.setTextColor(102, 102, 102);
    doc.text(`Reiseziel: ${formData.country}`, 14, 35);
    doc.text(`Reisende: ${formData.travelers} Person(en)`, 14, 42);
    doc.text(`Reisedauer: ${formData.days} Tage`, 14, 49);
    doc.text(`Gepäck: ${formData.packingPreference === 'lightweight' ? 'Leicht' : formData.packingPreference === 'checked' ? 'Aufgabegepäck' : 'Rucksack'}`, 14, 56);
    
    let yPosition = 70;
    
    // Packing list sections
    Object.entries(packingList).forEach(([category, items]) => {
      if (items.length > 0) {
        // Category title
        doc.setFontSize(14);
        doc.setTextColor(78, 205, 196); // Teal
        const categoryName = category === 'basics' ? 'Basics' : 
                           category === 'clothing' ? 'Kleidung' : 
                           category === 'toiletries' ? 'Hygiene' : 
                           category === 'tech' ? 'Technik' : 'Aktivitäten';
        doc.text(categoryName, 14, yPosition);
        yPosition += 7;
        
        // Items
        doc.setFontSize(10);
        doc.setTextColor(45, 49, 66);
        items.forEach(item => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          const isChecked = checkedItems[`${category}-${items.indexOf(item)}`];
          doc.text(isChecked ? '☑ ' + item : '☐ ' + item, 20, yPosition);
          yPosition += 6;
        });
        yPosition += 5;
      }
    });
    
    // Add new page for advice
    doc.addPage();
    yPosition = 20;
    
    // Clothing advice
    doc.setFontSize(14);
    doc.setTextColor(78, 205, 196);
    doc.text(`Kleiderregeln für ${formData.country}`, 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(45, 49, 66);
    const adviceText = doc.splitTextToSize(advice.advice, pageWidth - 28);
    doc.text(adviceText, 14, yPosition);
    yPosition += adviceText.length * 6 + 10;
    
    if (advice.noGos.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(255, 107, 107); // Coral
      doc.text("No-Go's:", 14, yPosition);
      yPosition += 7;
      
      doc.setFontSize(10);
      doc.setTextColor(45, 49, 66);
      advice.noGos.forEach(nogo => {
        const nogoText = doc.splitTextToSize('✗ ' + nogo, pageWidth - 28);
        doc.text(nogoText, 14, yPosition);
        yPosition += nogoText.length * 6 + 2;
      });
      yPosition += 5;
    }
    
    // Layering system
    yPosition += 10;
    doc.setFontSize(14);
    doc.setTextColor(78, 205, 196);
    doc.text('Zwiebelprinzip - Schichten kombinieren', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(45, 49, 66);
    doc.text('Basisschicht: ' + layering.base, 14, yPosition);
    yPosition += 7;
    doc.text('Mittelschicht: ' + layering.mid, 14, yPosition);
    yPosition += 7;
    doc.text('Außenschicht: ' + layering.outer, 14, yPosition);
    yPosition += 7;
    doc.text('Unten: ' + layering.bottom, 14, yPosition);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Just Travel - Dein Reise-Guide | just-travel.com', pageWidth / 2, 285, { align: 'center' });
    
    // Save PDF
    doc.save(`Packliste-${formData.country}-${formData.days}Tage.pdf`);
  };

  return (
    <section id="packing" className="section packing-section">
      <div className="packing-header">
        <h2 className="section-title">Interaktiver Packlisten-Generator</h2>
        <p className="section-subtitle">
          Erstelle deine perfekte Packliste basierend auf Reiseziel, Aktivitäten und Gepäckpräferenz
        </p>
      </div>

      <div className="packing-container">
        <div className="packing-form card">
          <h3 className="form-title">
            <Package size={24} />
            Deine Reisedetails
          </h3>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <Plane size={18} />
                Reiseziel
              </label>
              <select 
                className="form-select"
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              >
                {countries.map(country => (
                  <option key={country} value={country.toLowerCase()}>{country}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Users size={18} />
                Anzahl Reisende
              </label>
              <input 
                type="number"
                className="form-input"
                value={formData.travelers}
                onChange={(e) => setFormData(prev => ({ ...prev, travelers: parseInt(e.target.value) || 1 }))}
                min="1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Calendar size={18} />
                Reisedauer (Tage)
              </label>
              <input 
                type="number"
                className="form-input"
                value={formData.days}
                onChange={(e) => setFormData(prev => ({ ...prev, days: parseInt(e.target.value) || 1 }))}
                min="1"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Weight size={18} />
                Gepäckpräferenz
              </label>
              <select 
                className="form-select"
                value={formData.packingPreference}
                onChange={(e) => setFormData(prev => ({ ...prev, packingPreference: e.target.value }))}
              >
                <option value="lightweight">Leichtes Gepäck & kleines Packmaß</option>
                <option value="checked">Aufgabegepäck bis 20kg</option>
                <option value="backpack">Rucksack</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Transportmittel (Mehrfachauswahl)</label>
            <div className="checkbox-grid">
              {transportOptions.map(option => (
                <label key={option} className="checkbox-label">
                  <input 
                    type="checkbox"
                    checked={formData.transport.includes(option.toLowerCase())}
                    onChange={() => handleMultiSelect('transport', option.toLowerCase())}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Unterkunft (Mehrfachauswahl)</label>
            <div className="checkbox-grid">
              {accommodationOptions.map(option => (
                <label key={option} className="checkbox-label">
                  <input 
                    type="checkbox"
                    checked={formData.accommodation.includes(option.toLowerCase())}
                    onChange={() => handleMultiSelect('accommodation', option.toLowerCase())}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <Activity size={18} />
              Aktivitäten (Mehrfachauswahl)
            </label>
            <div className="checkbox-grid">
              {activityOptions.map(option => (
                <label key={option} className="checkbox-label">
                  <input 
                    type="checkbox"
                    checked={formData.activities.includes(option.toLowerCase())}
                    onChange={() => handleMultiSelect('activities', option.toLowerCase())}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          <button className="btn-primary generate-btn" onClick={generatePackingList}>
            <Package size={20} />
            Packliste generieren
          </button>
        </div>

        {packingList && (
          <div className="packing-result">
            <div className="packing-list card">
              <div className="list-header">
                <h3>Deine Packliste</h3>
                <button className="btn-outline download-btn" onClick={downloadPDF}>
                  <Download size={18} />
                  Als PDF herunterladen
                </button>
              </div>

              {Object.entries(packingList).map(([category, items]) => (
                items.length > 0 && (
                  <div key={category} className="list-category">
                    <h4 className="category-title">{category === 'basics' ? 'Basics' : category === 'clothing' ? 'Kleidung' : category === 'toiletries' ? 'Hygiene' : category === 'tech' ? 'Technik' : 'Aktivitäten'}</h4>
                    <ul className="item-list">
                      {items.map((item, index) => (
                        <li key={index} className="item">
                          <label className="item-checkbox">
                            <input 
                              type="checkbox"
                              checked={checkedItems[`${category}-${index}`] || false}
                              onChange={() => toggleCheck(category, index)}
                            />
                            <CheckCircle2 size={20} className={checkedItems[`${category}-${index}`] ? 'checked' : ''} />
                            <span className={checkedItems[`${category}-${index}`] ? 'checked-text' : ''}>{item}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
            </div>

            <div className="advice-cards">
              <div className="advice-card card">
                <h4 className="advice-title">Kleiderregeln für {formData.country}</h4>
                <p className="advice-text">{advice.advice}</p>
                {advice.noGos.length > 0 && (
                  <>
                    <h5 className="advice-subtitle">No-Go's:</h5>
                    <ul className="advice-list">
                      {advice.noGos.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="advice-card card">
                <h4 className="advice-title">Zwiebelprinzip - Schichten kombinieren</h4>
                <div className="layering-grid">
                  <div className="layer-item">
                    <strong>Basisschicht:</strong>
                    <p>{layering.base}</p>
                  </div>
                  <div className="layer-item">
                    <strong>Mittelschicht:</strong>
                    <p>{layering.mid}</p>
                  </div>
                  <div className="layer-item">
                    <strong>Außenschicht:</strong>
                    <p>{layering.outer}</p>
                  </div>
                  <div className="layer-item">
                    <strong>Unten:</strong>
                    <p>{layering.bottom}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default PackingGenerator;
